// OTPForm.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import dayjs from 'dayjs';
import { useAuthStore } from '../../../stores/auth';
import { Loader2 } from 'lucide-react';

interface OTPFormInputs {
    otp: string;
    password: string;
}

const otpSchema = yup.object().shape({
    otp: yup
        .string()
        // .matches(/^\d{6}$/, 'OTP phải là 6 chữ số')
        .required('OTP là bắt buộc'),
    password: yup
        .string()
        .required('Mật khẩu là bắt buộc')
        .min(6, 'Mật khẩu phải có ít nhất 6 ký tự'),
});

interface OTPFormProps {
    onCancel?: () => void;
}

const OTPForm: React.FC<OTPFormProps> = React.memo(({ onCancel }) => {
    const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<OTPFormInputs>({
        resolver: yupResolver(otpSchema),
    });

    const { otp, verifyForgotPassword, forgotPassword, loading } = useAuthStore();

    const [timeCount, setTimeCount] = useState(0);

    // lấy time hiện tại trừ cho timeEx dùng dayjs
    useEffect(() => {
        const interval = setInterval(() => {
            setTimeCount(dayjs(otp?.timeEx).diff(dayjs(), 'seconds'));
            // console.log(`Server time: ${otp?.timeEx}, Parsed time: ${dayjs(otp?.timeEx).format()}, Current time: ${dayjs().format()}, Diff in seconds: ${dayjs(otp?.timeEx).diff(dayjs(), "seconds")}`);
        }, 1000);
        return () => clearInterval(interval);
    }, [otp]);

    const onSubmit: SubmitHandler<OTPFormInputs> = useCallback(async (data) => {
        verifyForgotPassword(otp?.email || '', data.otp, data.password);
    }, [verifyForgotPassword, otp]);

    const onResendOTP = useCallback(() => {
        forgotPassword(otp?.email || '');
    }, [forgotPassword, otp]);


    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 w-full py-5">
            <div>
                <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                    Nhập OTP
                </label>
                <Input
                    id="otp"
                    type="text"
                    placeholder="Nhập OTP 6 chữ số"
                    {...register('otp')}
                    className="mt-1"
                />

                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Mật khẩu mới
                </label>
                <Input
                    id="password"
                    type="password"
                    placeholder="Nhập mật khẩu mới"
                    {...register('password')}
                    className="mt-1"
                />
                {errors.otp && <p className="text-red-500 text-xs mt-1">{errors.otp.message}</p>}
            </div>
            {
                timeCount > 0 && (
                    <div className="flex justify-start items-center w-full">

                        <p className="text-sm text-gray-500">
                            OTP sẽ hết hạn trong {timeCount} giây
                        </p>
                    </div>
                )
            }

            {
                timeCount <= 0 && (
                    <div className="flex justify-start items-center w-full">

                        <p className="text-sm text-gray-500 cursor-pointer underline" onClick={() => onResendOTP()}>
                            Gửi lại OTP
                        </p>
                    </div>
                )
            }

            <div className="flex gap-2">
                <Button type="submit" disabled={loading} className="w-full text-blue-500">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Xác nhận OTP'}
                </Button>
                {onCancel && <Button onClick={onCancel} disabled={isSubmitting} className="w-full border-red-200 text-red-500" variant="outline">
                    {'Huỷ.'}
                </Button>}
            </div>
        </form>
    );
});

export default OTPForm;